import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { useAuth } from '@/context/AuthContext';
import { parseServerUtc } from '@/utils/dateUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Jeda minimal antar pengecekan ke server saat nakes pindah halaman. Dibuat longgar
// supaya tidak menambah beban server (insiden lemot Okt 2026).
const MIN_REFETCH_MS = 60 * 1000;
const REQUEST_TIMEOUT_MS = 20000;

// Halaman surat sendiri: nakes sudah di tempat yang tepat, jangan ganggu.
const isLettersPage = (path) => path.startsWith('/nakes/warning-letters');
// Layar job yang sedang dikerjakan (workflow, stok, konfirmasi layanan, upload dokumen).
// Pop up tidak boleh memotong nakes saat melayani pasien. Daftar job dan job bersama tetap kena.
const isActiveJobPage = (path) => /^\/nakes\/jobs\/(?!shared(\/|$))[^/]+/.test(path);
// Dashboard sudah punya kartu peringatan sendiri, jadi banner tidak perlu dobel.
const isDashboard = (path) => path === '/nakes' || path === '/nakes/' || path === '/nakes/dashboard';

const LEVEL_LABEL = {
  TEGURAN: 'Surat Teguran',
  SP1: 'Surat Peringatan 1',
  SP2: 'Surat Peringatan 2',
  SP3: 'Surat Peringatan 3',
  FATAL: 'Surat Pelanggaran Fatal',
  PROBATION: 'Surat Probation',
};

function hoursLeft(letter) {
  const d = parseServerUtc(letter?.ack_deadline_at);
  if (!d) return null;
  return Math.round(((d.getTime() - Date.now()) / 3600000) * 10) / 10;
}

function deadlineText(letter) {
  if (letter.ack_status === 'overdue') return 'TERLAMBAT, segera konfirmasi';
  const h = hoursLeft(letter);
  if (h === null) return 'Batas konfirmasi 48 jam setelah surat dikirim';
  if (h <= 0) return 'TERLAMBAT, segera konfirmasi';
  return `Sisa waktu konfirmasi: ${h} jam`;
}

/**
 * Banner dan pop up WAJIB untuk surat peringatan yang belum dikonfirmasi (Nov 2026).
 *
 * - Banner kuning/merah menempel di semua halaman area nakes (kecuali dashboard dan
 *   halaman surat), tidak bisa ditutup.
 * - Pop up tidak bisa ditutup. Satu-satunya jalan keluar adalah membuka surat dan
 *   menekan "Saya Telah Membaca dan Memahami". Pop up TIDAK muncul di layar job yang
 *   sedang dikerjakan, supaya pelayanan pasien tidak terganggu.
 * - Mengecek ke server saat layout dibuka, saat pindah halaman (maksimal 1x per menit),
 *   dan langsung setelah nakes menekan konfirmasi.
 */
export default function NakesMandatoryLetterGate() {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [letters, setLetters] = useState([]);
  const lastFetchRef = useRef(0);
  const inFlightRef = useRef(false);
  const requestSeqRef = useRef(0); // abaikan respons lama yang selesai setelah konfirmasi

  const fetchLetters = useCallback(async (force = false) => {
    const token = localStorage.getItem('token');
    if (!token) return;
    if (!force && (inFlightRef.current || Date.now() - lastFetchRef.current < MIN_REFETCH_MS)) return;
    const seq = ++requestSeqRef.current; // permintaan paksa membuat permintaan lama tidak berlaku
    inFlightRef.current = true;
    try {
      const res = await axios.get(`${API}/nakes/my-warning-letters`, {
        params: { limit: 50 },
        timeout: REQUEST_TIMEOUT_MS,
        headers: { Authorization: `Bearer ${token}` },
      });
      if (seq !== requestSeqRef.current) return; // sudah ada permintaan yang lebih baru
      const items = Array.isArray(res.data) ? res.data : (res.data?.items || []);
      // Sama persis dengan aturan blokir apply job di backend.
      setLetters(items.filter((l) => l.status === 'sent' && (l.ack_status === 'pending' || l.ack_status === 'overdue')));
      lastFetchRef.current = Date.now();
    } catch (_e) {
      /* diam: sinyal lemah tidak boleh membuat aplikasi macet atau memunculkan error */
    } finally {
      if (seq === requestSeqRef.current) inFlightRef.current = false;
    }
  }, []);

  // Cek saat layout dibuka dan setiap pindah halaman (dibatasi jeda).
  useEffect(() => {
    fetchLetters(false);
  }, [location.pathname, fetchLetters]);

  // Setelah konfirmasi di halaman surat: perbarui langsung.
  useEffect(() => {
    const onChanged = () => {
      setLetters([]); // hilangkan dulu agar terasa instan, lalu sinkron ke server
      fetchLetters(true);
    };
    window.addEventListener('vaxa:letters-changed', onChanged);
    return () => window.removeEventListener('vaxa:letters-changed', onChanged);
  }, [fetchLetters]);

  const path = location.pathname;
  if (letters.length === 0 || isLettersPage(path)) return null;

  const hasOverdue = letters.some((l) => l.ack_status === 'overdue' || (hoursLeft(l) !== null && hoursLeft(l) <= 0));
  const showModal = !isActiveJobPage(path);
  const showBanner = !isDashboard(path);

  return (
    <>
      {showBanner && (
        <div
          className={`${hasOverdue ? 'bg-red-50 border-red-300 text-red-900' : 'bg-yellow-50 border-yellow-300 text-yellow-900'} border-b-2 px-4 py-2.5 flex items-center gap-3 text-sm`}
          data-testid="mandatory-letter-banner"
          role="alert"
        >
          <AlertTriangle className={`w-5 h-5 flex-shrink-0 ${hasOverdue ? 'text-red-600' : 'text-yellow-600'}`} />
          <div className="flex-1 min-w-0">
            <p className="font-semibold">
              {hasOverdue
                ? `Anda punya ${letters.length} surat resmi yang BELUM dikonfirmasi dan sudah terlambat`
                : `Anda punya ${letters.length} surat resmi yang belum dikonfirmasi`}
            </p>
            <p className="text-xs mt-0.5">Anda tidak bisa mengambil job baru sampai surat dikonfirmasi.</p>
          </div>
          <Link
            to="/nakes/warning-letters"
            className={`${hasOverdue ? 'bg-red-600 hover:bg-red-700' : 'bg-yellow-500 hover:bg-yellow-600'} text-white text-xs font-semibold rounded px-3 py-1.5 flex-shrink-0`}
            data-testid="mandatory-letter-banner-link"
          >
            Buka Surat
          </Link>
        </div>
      )}

      <Dialog open={showModal} onOpenChange={() => { /* sengaja tidak bisa ditutup */ }}>
        <DialogContent
          className="max-w-md [&>button]:hidden"
          onEscapeKeyDown={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          data-testid="mandatory-letter-modal"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" />
              Anda menerima surat resmi dari manajemen
            </DialogTitle>
            <DialogDescription>
              Ini bukan pesan biasa. Surat ini dicatat resmi dan wajib Anda baca lalu konfirmasi.
              Selama belum dikonfirmasi, Anda tidak dapat mengambil job baru.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2 max-h-60 overflow-y-auto">
            {letters.map((l) => (
              <div key={l.letter_id} className="rounded border border-red-200 bg-red-50 p-3 text-sm">
                <p className="font-semibold text-red-900">{LEVEL_LABEL[l.level] || l.level}</p>
                {l.subject && <p className="text-red-800">{l.subject}</p>}
                <p className="text-xs text-red-700 mt-1">{deadlineText(l)}</p>
              </div>
            ))}
          </div>

          <DialogFooter className="gap-2 sm:flex-col sm:space-x-0">
            <Button
              className="w-full bg-red-600 hover:bg-red-700 text-white"
              onClick={() => navigate('/nakes/warning-letters')}
              data-testid="mandatory-letter-modal-open"
            >
              Buka dan Baca Surat Sekarang
            </Button>
            <Button
              variant="ghost"
              className="w-full text-xs text-gray-500"
              onClick={() => { logout(); navigate('/'); }}
              data-testid="mandatory-letter-modal-logout"
            >
              Keluar dari akun
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
